import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import RoleLensSelector from "@/components/shared/RoleLensSelector";
import { DemoPathSelector } from "@/components/shared/LiveDemoTour";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, CheckCircle2, Lock, Truck, FileText, Sparkles,
  ArrowRight, ChevronRight, Mail, FileUp, Target, BarChart3,
  DollarSign, Users, Calendar, MessageSquare, Shield, Eye,
  TrendingDown, Send, Zap, Plus, Building2, Activity
} from "lucide-react";
import OnSiteNowBoard from "@/components/shared/OnSiteNowBoard";

function StatCard({ label, value, sub, icon: Icon, color, borderColor, bgColor, onClick }) {
  return (
    <button onClick={onClick} className={`text-left ${bgColor || 'bg-white'} rounded-xl border ${borderColor || 'border-slate-200'} p-4 hover:shadow-md transition-all group w-full`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${color || 'text-slate-500'}`} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      </div>
      <p className={`text-2xl font-heading font-bold ${color || 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </button>
  );
}

export default function BuilderOverview() {
  const { projects, blockers, trades, conditions, evidence, gates, calendarEvents, builderStats, workspace, analytics, activeLens, lens, actionLog, people, resolveBlocker, sendChase } = useDemoState();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);

  const atRiskProjects = projects.filter(p => p.status === 'at-risk');
  const criticalBlockers = blockers.filter(b => b.severity === 'critical');
  const heldTrades = trades.filter(t => t.status === 'hold');
  const pendingEvidence = evidence.filter(e => e.status === 'needs-review' || e.status === 'pending-match');

  // "If you do nothing today" computation
  const inactionImpact = useMemo(() => {
    const impacts = [];
    criticalBlockers.forEach(b => {
      if (b.trade) impacts.push({ text: `${b.trade} booking window closes if "${b.title}" not resolved`, severity: 'critical', owner: b.owner });
    });
    heldTrades.forEach(t => {
      impacts.push({ text: `${t.trade} (${t.company}) remains on hold. Re-mobilisation risk.`, severity: 'high', owner: t.contact });
    });
    return impacts;
  }, [criticalBlockers, heldTrades]);

  // Lens-aware priority actions
  const priorityActions = useMemo(() => {
    if (activeLens === 'office-admin') {
      return [
        ...pendingEvidence.slice(0, 2).map(e => ({ text: `Review ${e.fileName}`, path: '/builder/proof-review', icon: Eye })),
        ...blockers.filter(b => b.chaseCount === 0).slice(0, 2).map(b => ({ text: `Send first chase to ${b.owner}`, path: '/builder/job-blocker', icon: Send })),
      ];
    }
    if (activeLens === 'site-supervisor') {
      return [
        { text: 'Upload site photos for sediment controls', path: '/builder/proof-review', icon: FileUp },
        { text: 'Check stage pack completeness', path: '/builder/site-ready-pack', icon: Shield },
      ];
    }
    if (activeLens === 'client') {
      return [{ text: 'View your project progress', path: '/shared/client-portal', icon: Eye }, { text: 'Confirm tile selection', path: '/builder/scope-boundary-ledger', icon: Target }];
    }
    return [
      ...criticalBlockers.slice(0, 2).map(b => ({ text: `Chase ${b.owner}: ${b.title}`, path: '/builder/job-blocker', icon: AlertTriangle })),
      ...heldTrades.slice(0, 1).map(t => ({ text: `Review ${t.trade} release status`, path: '/builder/trade-release', icon: Truck })),
      ...pendingEvidence.slice(0, 1).map(e => ({ text: `Review ${e.fileName}`, path: '/builder/proof-review', icon: Eye })),
    ];
  }, [activeLens, criticalBlockers, heldTrades, pendingEvidence, blockers]);

  const proj = selectedProject ? projects.find(p => p.id === selectedProject) : null;
  const projBlockers = proj ? blockers.filter(b => b.projectId === proj.id) : [];
  const projGate = proj ? gates.find(g => g.projectId === proj.id && g.state === 'locked') : null;

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="builder-overview">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-heading font-bold text-slate-900">Command Centre</h1>
            <p className="text-xs text-slate-500 mt-0.5">{lens.priority}</p>
          </div>
          <div className="flex items-center gap-2">
            <DemoPathSelector />
            <Button size="sm" className="bg-blue-600 hover:bg-blue-500 gap-1.5 text-xs" onClick={() => navigate('/builder/project-intake')} data-testid="create-project-btn">
              <Plus className="w-3.5 h-3.5" /> Create project
            </Button>
          </div>
        </div>

        <RoleLensSelector />

        {/* Stats strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="stats-strip">
          <StatCard label="At risk" value={atRiskProjects.length} sub={`${projects.length} total projects`} icon={AlertTriangle} color={atRiskProjects.length > 0 ? 'text-red-600' : 'text-emerald-600'} borderColor={atRiskProjects.length > 0 ? 'border-red-200' : 'border-emerald-200'} bgColor={atRiskProjects.length > 0 ? 'bg-red-50/50' : 'bg-emerald-50/50'} />
          <StatCard label="Blocked stages" value={gates.filter(g => g.state === 'locked').length} sub="stages cannot proceed" icon={Lock} color="text-amber-600" borderColor="border-amber-200" bgColor="bg-amber-50/50" onClick={() => navigate('/builder/stage-readiness')} />
          <StatCard label="Trades on hold" value={heldTrades.length} sub={`${trades.filter(t => t.status === 'released').length} released`} icon={Truck} color={heldTrades.length > 0 ? 'text-red-600' : 'text-emerald-600'} borderColor={heldTrades.length > 0 ? 'border-red-200' : 'border-emerald-200'} bgColor={heldTrades.length > 0 ? 'bg-red-50/50' : 'bg-emerald-50/50'} onClick={() => navigate('/builder/trade-release')} />
          <StatCard label="Evidence pending" value={pendingEvidence.length} sub="awaiting review" icon={Eye} color="text-blue-600" borderColor="border-blue-200" bgColor="bg-blue-50/50" onClick={() => navigate('/builder/proof-review')} />
          <StatCard label="Conditions" value={`${builderStats.conditionsCleared}/${builderStats.conditionsTotal}`} sub={`${Math.round(builderStats.conditionsCleared / Math.max(builderStats.conditionsTotal, 1) * 100)}% cleared`} icon={CheckCircle2} color="text-emerald-600" borderColor="border-emerald-200" bgColor="bg-emerald-50/50" onClick={() => navigate('/builder/stage-conditions')} />
        </div>

        {/* On-Site Now Board */}
        {activeLens !== 'client' && activeLens !== 'buyer' && <OnSiteNowBoard />}

        {/* If you do nothing today */}
        {inactionImpact.length > 0 && activeLens !== 'client' && activeLens !== 'buyer' && (
          <Card className="border-red-200 bg-red-50/30" data-testid="inaction-impact">
            <CardContent className="py-3.5 px-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <span className="text-xs font-bold text-red-700">If you do nothing today</span>
              </div>
              <div className="space-y-1.5">
                {inactionImpact.slice(0, 3).map((imp, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-red-700">
                    <span className="w-4 h-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                    {imp.text}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Priority actions */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-slate-200" data-testid="priority-actions">
              <CardContent className="py-4 px-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-blue-600" /><span className="text-sm font-bold text-slate-900">Priority actions</span></div>
                  <Badge className="text-[9px] bg-blue-50 text-blue-600">{lens.label} view</Badge>
                </div>
                <div className="space-y-2">
                  {priorityActions.map((a, i) => (
                    <Link key={i} to={a.path} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-blue-100"><a.icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-blue-600" /></div>
                      <span className="text-xs font-medium text-slate-700 group-hover:text-blue-600 flex-1">{a.text}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-400" />
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Project cards */}
            <div className="space-y-3" data-testid="project-cards">
              {projects.map(p => {
                const pBlockers = blockers.filter(b => b.projectId === p.id);
                const pGate = gates.find(g => g.projectId === p.id && g.state === 'locked');
                const statusCfg = p.status === 'at-risk' ? { bg: 'bg-red-50/50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' } : p.status === 'on-track' ? { bg: 'bg-white', border: 'border-slate-200', badge: 'bg-emerald-100 text-emerald-700' } : p.status === 'complete' ? { bg: 'bg-slate-50', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' } : { bg: 'bg-amber-50/50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' };
                return (
                  <Card key={p.id} className={`${statusCfg.border} ${statusCfg.bg} hover:shadow-md transition-all cursor-pointer`} onClick={() => setSelectedProject(p.id)} data-testid={`project-card-${p.id}`}>
                    <CardContent className="py-3.5 px-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-slate-900">{p.name}</h3>
                            <Badge className={`text-[9px] ${statusCfg.badge}`}>{p.status === 'at-risk' ? 'At risk' : p.status === 'on-track' ? 'On track' : p.status === 'on-hold' ? 'On hold' : 'Complete'}</Badge>
                          </div>
                          <p className="text-[10px] text-slate-500">{p.address} · {p.council} · {p.permitNumber}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-heading font-bold text-slate-900">{p.completionPercent}%</p>
                          <p className="text-[9px] text-slate-400">complete</p>
                        </div>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2">
                        <div className={`h-full rounded-full ${p.status === 'at-risk' ? 'bg-red-500' : p.status === 'complete' ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${p.completionPercent}%` }} />
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] text-slate-500 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> {p.stage}</span>
                        {p.blockerCount > 0 && <span className="text-[10px] text-red-600 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> {p.blockerCount} blocker{p.blockerCount !== 1 ? 's' : ''}</span>}
                        {pGate && <span className="text-[10px] text-amber-600 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> {pGate.stage} locked</span>}
                        <span className="text-[10px] text-slate-400 flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {p.owner}</span>
                        <span className="text-[10px] text-slate-400 ml-auto">{p.lastActivity}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            {/* Analytics compact */}
            {activeLens !== 'client' && activeLens !== 'buyer' && analytics && (
              <Card className="border-slate-200" data-testid="analytics-compact">
                <CardContent className="py-3.5 px-4">
                  <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-slate-500" /><span className="text-xs font-bold text-slate-700">Operational insights</span></div>
                  <div className="space-y-2.5">
                    {analytics.topBlockerCause && (
                      <div className="bg-red-50 rounded-lg p-2.5 border border-red-100">
                        <p className="text-[9px] font-semibold uppercase text-red-600">Top blocker cause</p>
                        <p className="text-xs font-bold text-red-700">{analytics.topBlockerCause.cause.replace(/-/g, ' ')}</p>
                        <p className="text-[10px] text-red-600">{analytics.topBlockerCause.count} active</p>
                      </div>
                    )}
                    {analytics.topOverdueOwner && (
                      <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                        <p className="text-[9px] font-semibold uppercase text-amber-600">Most overdue owner</p>
                        <p className="text-xs font-bold text-amber-700">{analytics.topOverdueOwner.owner}</p>
                        <p className="text-[10px] text-amber-600">{analytics.topOverdueOwner.count} item(s)</p>
                      </div>
                    )}
                    {analytics.tradeFriction.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                        <p className="text-[9px] font-semibold uppercase text-slate-500">Trade friction</p>
                        {analytics.tradeFriction.slice(0, 2).map((t, i) => (
                          <p key={i} className="text-[10px] text-slate-600">{t.trade}: {t.blockers} blocker(s)</p>
                        ))}
                      </div>
                    )}
                    {analytics.ownerRisk.length > 0 && (
                      <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-100">
                        <p className="text-[9px] font-semibold uppercase text-purple-600">Owner-side risk</p>
                        {analytics.ownerRisk.slice(0, 2).map((r, i) => (
                          <p key={i} className="text-[10px] text-purple-600">{r.item}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Activity log */}
            <Card className="border-slate-200" data-testid="activity-log">
              <CardContent className="py-3.5 px-4">
                <div className="flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-slate-500" /><span className="text-xs font-bold text-slate-700">Recent activity</span></div>
                <div className="space-y-2">
                  {(actionLog || []).slice(0, 6).map((a, i) => (
                    <div key={a.id || i} className="flex items-start gap-2 text-[10px]">
                      <span className="text-slate-400 shrink-0 w-10">{a.time}</span>
                      <span className="text-slate-600">{a.action}</span>
                    </div>
                  ))}
                  {(!actionLog || actionLog.length === 0) && <p className="text-[10px] text-slate-400">No recent actions</p>}
                </div>
              </CardContent>
            </Card>

            {/* Quick links */}
            <Card className="border-slate-200">
              <CardContent className="py-3.5 px-4 space-y-1.5">
                {[
                  { path: '/builder/stage-readiness', label: 'Stage Readiness', icon: Lock },
                  { path: '/builder/trade-release', label: 'Trade Release', icon: Truck },
                  { path: '/builder/release-calendar', label: 'Calendar', icon: Calendar },
                  { path: '/builder/readiness-dossier', label: 'Readiness Dossier', icon: FileText },
                  { path: '/builder/whatsapp-action-rail', label: 'WhatsApp Rail', icon: MessageSquare },
                ].map(l => (
                  <Link key={l.path} to={l.path} className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group">
                    <l.icon className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-600" />
                    <span className="text-xs text-slate-600 group-hover:text-blue-600 font-medium">{l.label}</span>
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Project detail drawer */}
        <Sheet open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
          <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="project-detail-drawer">
            {proj && (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" />{proj.name}</SheetTitle>
                  <SheetDescription>{proj.address} · {proj.council}</SheetDescription>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Stage', value: proj.stage },
                      { label: 'Permit', value: proj.permitNumber },
                      { label: 'PM', value: proj.owner },
                      { label: 'Complete', value: `${proj.completionPercent}%` },
                    ].map(s => (
                      <div key={s.label} className="bg-slate-50 rounded-lg p-2.5"><p className="text-[9px] text-slate-400 font-semibold uppercase">{s.label}</p><p className="text-xs font-medium text-slate-700">{s.value}</p></div>
                    ))}
                  </div>
                  {projBlockers.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Blockers ({projBlockers.length})</p>
                      {projBlockers.map(b => (
                        <div key={b.id} className="p-2.5 bg-red-50 border border-red-100 rounded-lg mb-1.5">
                          <p className="text-xs font-medium text-red-800">{b.title}</p>
                          <p className="text-[10px] text-red-600">{b.owner} · {b.severity} · Due {b.dueDate}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {projGate && (
                    <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <p className="text-xs font-bold text-amber-700">{projGate.milestone} gate: Locked</p>
                      <p className="text-[10px] text-amber-600">{projGate.clearedConditions}/{projGate.totalConditions} cleared</p>
                    </div>
                  )}
                  {proj.activityTimeline?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Timeline</p>
                      {proj.activityTimeline.map((a, i) => (
                        <div key={i} className="flex items-start gap-2 text-[10px] mb-1.5">
                          <span className="text-slate-400 shrink-0 w-16">{a.date}</span>
                          <span className="text-slate-600">{a.action}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Separator />
                  <div className="flex gap-2 flex-wrap">
                    <Link to="/builder/stage-readiness"><Button variant="outline" size="sm" className="text-xs"><Lock className="w-3.5 h-3.5 mr-1" />Readiness</Button></Link>
                    <Link to="/builder/trade-release"><Button variant="outline" size="sm" className="text-xs"><Truck className="w-3.5 h-3.5 mr-1" />Trades</Button></Link>
                    <Link to="/builder/job-blocker"><Button variant="outline" size="sm" className="text-xs"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Blockers</Button></Link>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
